/* eslint-disable */
import { LOCATION_CHANGE } from 'connected-react-router';
import addDays from 'date-fns/add_days';
import subDays from 'date-fns/sub_days';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import some from 'lodash/some';
import moment from 'moment';
import { REHYDRATE } from 'redux-persist';
import { FILTER_DEFAULT_NUMBER_OF_DAYS, FILTER_PERSIST_KEY } from '../../../Constants/Filter/Filter.constants';
import {
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_CONFIRMED,
  ORDER_STATUS_DELIVERING,
  ORDER_STATUS_ON_HOLD,
  ORDER_STATUS_TO_BE_CONFIRMED,
} from '../../../Constants/Order/Order.constants';
import startOfToday from '../../../Support/DateTime/StartOfToday/DateTimeStartOfToday.support';
import { AUTH_STATE_CHANGED_NEXT } from '../Auth/Auth.types';
import {
  FILTER_ACCOUNT_SELECTED,
  FILTER_ADDRESS_ADDED,
  FILTER_ADDRESS_REMOVED,
  FILTER_ADDRESS_REMOVED_ALL,
  FILTER_ADDRESS_SELECTED,
  FILTER_ADDRESS_TOGGLED_ALL,
  FILTER_ADDRESS_UNSELECTED,
  FILTER_NEXT_DATE_RANGE_SELECTED,
  FILTER_NUMBER_OF_DAYS_SELECTED,
  FILTER_PREV_DATE_RANGE_SELECTED,
  FILTER_PROJECT_SELECTED,
  FILTER_START_DATE_SELECTED,
  FILTER_STATUS_SELECTED,
  FILTER_TODAY_DATE_SELECTED,
} from './Filters.types';
import { ORDERS_FIND_BY_ID_SUCCESS } from '../Orders/Orders.types';

/**
 * Create the state for the `filtersReducer` before `redux-persist` kicks in
 * @returns {any} The initial state before `redux-persist` kicks in
 */
const createPrePersistFiltersState = () => ({
  account: null,
  addresses: {},
  numberOfDays: FILTER_DEFAULT_NUMBER_OF_DAYS,
  orderStatus: [ORDER_STATUS_TO_BE_CONFIRMED, ORDER_STATUS_CONFIRMED, ORDER_STATUS_DELIVERING, ORDER_STATUS_COMPLETED, ORDER_STATUS_ON_HOLD],
  redirect: null,
  router: null,
  startDate: startOfToday(),
});

/**
 * Create the `filtersReducer`
 * @param {InjectablesForReducer} injectables The _injectable_'s required by this reducer
 * @returns {Reducer<State, FiltersActions>} The `filtersReducer`
 */
const filtersReducer = ({ authService }) => (state = createPrePersistFiltersState({ authService }), action) => {
  switch (action.type) {
    // WORKAROUND for iOS PWA remember last page:
    // If we can persist the router (from connected-router) using redux-persist
    // then we don't need to capture LOCATION_CHANGE action.type here.
    // For now just piggy back on the Filters Reducer to get the last navigation
    // link to solve the iOS Add To Home Screen issue where the app goes back to
    // the starting point instead of the last navigated page before leaving the app.
    case LOCATION_CHANGE: {
      return {
        ...state,
        redirect: null,
        router: action.payload,
      };
    }
    case REHYDRATE: {
      if (action.key !== FILTER_PERSIST_KEY || action.payload == null) {
        return state;
      }
      const { _persist, ...newState } = action.payload;
      // WORKAROUND for iOS PWA remember last page:
      // If we can persist the router (from connected-router) using redux-persist
      // then we don't need to capture LOCATION_CHANGE action.type here.
      // For now just piggy back on the Filters Reducer to get the last navigation
      // link to solve the iOS Add To Home Screen issue where the app goes back to
      // the starting point instead of the last navigated page before leaving the app.
      return {
        ...newState,
        redirect: action.payload.router,
      };
    }
    case AUTH_STATE_CHANGED_NEXT: {
      if (action.payload === null) {
        return createPrePersistFiltersState();
      }
      const { account } = state;
      const [defaultAccount] = authService.getGroups() || [];
      return {
        ...state,
        account: account || defaultAccount,
      };
    }
    case FILTER_NEXT_DATE_RANGE_SELECTED: {
      const { numberOfDays, startDate } = state;
      return {
        ...state,
        startDate: addDays(startDate, numberOfDays),
      };
    }
    case FILTER_NUMBER_OF_DAYS_SELECTED: {
      return {
        ...state,
        numberOfDays: action.payload,
      };
    }
    case FILTER_PREV_DATE_RANGE_SELECTED: {
      const { numberOfDays, startDate } = state;
      return {
        ...state,
        startDate: action.meta.startDate ? action.meta.startDate.toISOString() : subDays(startDate, numberOfDays),
      };
    }
    case FILTER_START_DATE_SELECTED: {
      return {
        ...state,
        startDate: action.payload,
      };
    }
    case FILTER_STATUS_SELECTED: {
      return {
        ...state,
        orderStatus: action.payload,
      };
    }
    case FILTER_TODAY_DATE_SELECTED: {
      return {
        ...state,
        startDate: moment(new Date()).startOf(`day`),
      };
    }
    case FILTER_ADDRESS_ADDED: {
      return {
        ...state,
        addresses: {
          ...state.addresses,
          [action.payload]: true,
        },
      };
    }
    case FILTER_ADDRESS_REMOVED: {
      return {
        ...state,
        addresses: omit(state.addresses, action.payload),
      };
    }
    case FILTER_ADDRESS_SELECTED: {
      return {
        ...state,
        addresses: {
          ...state.addresses,
          [action.payload]: true,
        },
      };
    }
    case FILTER_ADDRESS_UNSELECTED: {
      return {
        ...state,
        addresses: {
          ...state.addresses,
          [action.payload]: false,
        },
      };
    }
    case FILTER_ADDRESS_TOGGLED_ALL: {
      const addressFiltered = some(state.addresses, Boolean);
      return {
        ...state,
        addresses: mapValues(state.addresses, () => !addressFiltered),
      };
    }
    case FILTER_ADDRESS_REMOVED_ALL: {
      return {
        ...state,
        addresses: {},
      };
    }
    case FILTER_ACCOUNT_SELECTED: {
      const { account } = state;
      const selectedAccount = action.payload;
      if (account === selectedAccount) {
        return state;
      }
      return {
        ...state,
        account: selectedAccount,
        project: null, // reset project when account switched
        // suburb: null, // reset suburb when account switched
      };
    }
    case FILTER_PROJECT_SELECTED: {
      const { project } = state;
      const selectedProject = action.payload;
      if (project === selectedProject) {
        return state;
      }
      return {
        ...state,
        project: selectedProject,
      };
    }
    // also update global account after getting the order id
    case ORDERS_FIND_BY_ID_SUCCESS: {
      if (action.payload && action.payload.billToAccount) {
        return {
          ...state,
          account: action.payload.billToAccount,
        }
      }
    }
    default: {
      return state;
    }
  }
};

export default filtersReducer;
